/// <reference types="zone.js" />
import { metrics } from "@opentelemetry/api";
import type { Span } from "@opentelemetry/api";
import { logs } from "@opentelemetry/api-logs";
import { ZoneContextManager } from "@opentelemetry/context-zone-peer-dep";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { AggregationTemporalityPreference, OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { browserDetector } from "@opentelemetry/opentelemetry-browser-detector";
import { defaultResource, detectResources, resourceFromAttributes } from "@opentelemetry/resources";
import type { Resource } from "@opentelemetry/resources";
import { BatchLogRecordProcessor, LoggerProvider } from "@opentelemetry/sdk-logs";
import type { LogRecordProcessor, ReadWriteLogRecord } from "@opentelemetry/sdk-logs";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import { BatchSpanProcessor, NoopSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import type { ReadableSpan } from "@opentelemetry/sdk-trace-web";
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_SERVER_ADDRESS,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_URL_FULL,
  ATTR_URL_PATH,
} from "@opentelemetry/semantic-conventions";
import { ATTR_DEPLOYMENT_ENVIRONMENT, ATTR_SESSION_ID } from "@opentelemetry/semantic-conventions/incubating";
import { captureConsole } from "app/core/telemetry/console-capture";
import { httpRoute } from "app/core/telemetry/http-route";
import { replaySessionId } from "app/core/telemetry/replay-session";
import { telemetryUserAttributes } from "app/core/telemetry/telemetry-user";
import { reportWebVitals } from "app/core/telemetry/web-vitals";
import { environment } from "environments/environment";
import { Map, Record, RecordOf } from "immutable";
import { version } from "../package.json";

const ResourceParts: Record.Factory<{ host: string; path: string }> = Record({ host: "", path: "" });

const resourceParts = (url: string): RecordOf<{ host: string; path: string }> => {
  try {
    const parsed: URL = new URL(url, location.origin);
    // A data: URL's pathname is its whole payload.
    return /^https?:$/.test(parsed.protocol) ? ResourceParts({ host: parsed.host, path: parsed.pathname }) : ResourceParts();
  } catch {
    return ResourceParts();
  }
};

/** Names HTTP spans "{method} {route}", collapsing record ids so the names can be grouped. */
class RequestRouteSpanProcessor extends NoopSpanProcessor {
  override onStart(span: Span & ReadableSpan): void {
    const url: unknown = span.attributes[ATTR_URL_FULL];
    const method: unknown = span.attributes[ATTR_HTTP_REQUEST_METHOD];
    if (typeof url !== "string" || typeof method !== "string") {
      return;
    }

    const { path } = resourceParts(url);
    if (!path) {
      return;
    }

    span.setAttribute(ATTR_URL_PATH, path);
    span.updateName(`${method} ${httpRoute(path)}`);
  }
}

class ReplaySessionSpanProcessor extends NoopSpanProcessor {
  override onStart(span: Span): void {
    const sessionId: string | null = replaySessionId();
    if (sessionId) {
      span.setAttribute(ATTR_SESSION_ID, sessionId);
    }
  }
}

/** Runs every call into a batch processor in the root zone, so its export timers and callbacks never trigger change detection. */
const outsideAngular = <T extends object>(processor: T): T =>
  new Proxy(processor, {
    get: (target: T, key: string | symbol): unknown => {
      const value: unknown = Reflect.get(target, key);
      return typeof value === "function" ? (...args: unknown[]): unknown => Zone.root.run((): unknown => value.apply(target, args)) : value;
    },
  });

class UserSpanProcessor extends NoopSpanProcessor {
  override onStart(span: Span): void {
    span.setAttributes(telemetryUserAttributes());
  }
}

/** Stamps the page, replay session and user on every log record, keeping any the emitter captured itself. */
class SharedLogAttributesProcessor implements LogRecordProcessor {
  onEmit(record: ReadWriteLogRecord): void {
    record.setAttributes(
      Map({ [ATTR_URL_PATH]: location.pathname, [ATTR_SESSION_ID]: replaySessionId() ?? "", ...telemetryUserAttributes() })
        .filter((value: unknown, key: string): boolean => !!value && !(key in record.attributes))
        .toObject()
    );
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

const initialiseTelemetry = (): void => {
  // browser.* attributes make OneUptime classify this as RUM rather than a backend service.
  const resource: Resource = defaultResource()
    .merge(detectResources({ detectors: [browserDetector] }))
    .merge(
      resourceFromAttributes({
        [ATTR_SERVICE_NAME]: environment.oneUptime.serviceName,
        [ATTR_SERVICE_VERSION]: version,
        [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment.oneUptime.environment,
        [ATTR_DEPLOYMENT_ENVIRONMENT]: environment.oneUptime.environment,
        // Grafana Alloy routes telemetry to the OneUptime project named here.
        "oneuptime.project": "tablabs",
      })
    );

  // Grafana Alloy forwards to OneUptime with its own credentials, so no token is sent from the browser.
  const traceExporter: OTLPTraceExporter = new OTLPTraceExporter({
    url: `${environment.oneUptime.collector}/v1/traces`,
  });

  const tracerProvider: WebTracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new UserSpanProcessor(), new ReplaySessionSpanProcessor(), new RequestRouteSpanProcessor(), outsideAngular(new BatchSpanProcessor(traceExporter))],
  });

  tracerProvider.register({ contextManager: new ZoneContextManager() });

  const metricExporter: OTLPMetricExporter = new OTLPMetricExporter({
    url: `${environment.oneUptime.collector}/v1/metrics`,
    // Cumulative would re-export the whole running histogram every interval.
    temporalityPreference: AggregationTemporalityPreference.DELTA,
  });

  const meterProvider: MeterProvider = new MeterProvider({
    resource,
    readers: [new PeriodicExportingMetricReader({ exporter: metricExporter })],
  });
  metrics.setGlobalMeterProvider(meterProvider);

  const logExporter: OTLPLogExporter = new OTLPLogExporter({
    url: `${environment.oneUptime.collector}/v1/logs`,
  });

  logs.setGlobalLoggerProvider(
    new LoggerProvider({
      resource,
      processors: [new SharedLogAttributesProcessor(), outsideAngular(new BatchLogRecordProcessor({ exporter: logExporter }))],
    })
  );
  captureConsole();

  // Anchored to the API origin so a lookalike host such as app.testlab360.com.au.evil.com never matches.
  const apiOrigin: RegExp = new RegExp(`^${new URL(environment.baseURL).origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:/|$)`);

  registerInstrumentations({
    instrumentations: [
      // Network events on every resource span overflow the BatchSpanProcessor queue on a cold load.
      new DocumentLoadInstrumentation({
        ignoreNetworkEvents: true,
        applyCustomAttributesOnSpan: {
          resourceFetch: (span: Span, resource: PerformanceResourceTiming): void => {
            const { host, path } = resourceParts(resource.name);
            span.updateName(`Fetch ${host}`);
            span.setAttribute(ATTR_SERVER_ADDRESS, host);
            span.setAttribute(ATTR_URL_PATH, path);
          },
        },
      }),
      // Signature capture fetches a data: URL whose url.full would carry the drawn signature.
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [apiOrigin],
        ignoreUrls: [/^(?:data|blob):/, new RegExp(`^${environment.oneUptime.host.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:/|$)`)],
      }),
    ],
  });

  // Must follow setGlobalMeterProvider: a meter obtained before registration stays a no-op.
  reportWebVitals();

  // The metric reader does not flush on page hide, and INP and CLS are only recorded at pagehide.
  document.addEventListener("visibilitychange", (): void => {
    if (document.visibilityState === "hidden") {
      void meterProvider.forceFlush();
    }
  });
  addEventListener("pagehide", (): void => void meterProvider.forceFlush());
};

try {
  // A hidden auth iframe can boot a second copy of the SPA; telemetry from it is a duplicate stream.
  if (window.self === window.top) {
    initialiseTelemetry();
  }
} catch (error) {
  // Telemetry failing must never take the app down.
  console.error("OpenTelemetry initialisation failed; continuing without telemetry.", error);
}
